import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { IModelDataV3WithUrl } from '@df/catalog/model';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { Url } from '@df/core/service/url';
import { ICmsClientExtensionLoaderService } from '@icms/client/extension-loader/icms-client-extension-loader.service';
import { ICmsDraft } from '@icms/core/resource/draft/icms-draft';
import { ICmsDraftResource } from '@icms/core/resource/draft/icms-draft.resource';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ICmsContentDraftService {
    /**
     * Hash parameter name
     */
    private readonly hashName = '_draft';

    readonly initDraft$ = new BehaviorSubject<ICmsDraft | undefined>(undefined);

    constructor(
        app: App,
        private client: Client,
        private iCmsDraftResource: ICmsDraftResource,
        private hashStorageService: HashStorageService,
        private sessionStorage: SessionStorageService,
        private url: Url,
        router: Router,
        iCmsClientExtensionLoaderService: ICmsClientExtensionLoaderService
    ) {
        if (app.isPlatformBrowser) {
            this.load();
        }
        this.initDraft$.subscribe(draft => {
            this.sessionStorage.set(this.hashName, draft?.data?.hash);
            iCmsClientExtensionLoaderService.draft$.next(draft);
        });
        if (router.navigated) {
            this.hashStorageService.remove(this.hashName, true);
        } else {
            const subscription = router.events.subscribe(event => {
                if (event instanceof NavigationEnd) {
                    this.hashStorageService.remove(this.hashName, true);
                    subscription.unsubscribe();
                }
            });
        }
    }

    /**
     * Gets public link to activate draft
     */
    getShareLink(draft: ICmsDraft) {
        const target = draft.target;
        if (!draft.data) {
            throw new Error('Draft data is undefined');
        }
        const url =
            target.type === 'page'
                ? this.url.toUrl((target.data as IModelDataV3WithUrl).url)
                : location.protocol + '//' + location.host + location.pathname;
        return `${url}#${this.hashName}=${draft.data.hash}`;
    }

    reset() {
        if (this.initDraft$.value) {
            this.initDraft$.next(undefined);
        }
    }

    private async load() {
        const draftHash = this.hashStorageService.get(this.hashName) || this.sessionStorage.get(this.hashName);
        if (draftHash) {
            try {
                const draft = await this.iCmsDraftResource.findOne({ filter: { hash: draftHash }, verbosity: 1 });
                this.initDraft$.next(draft || undefined);
                if (draft && !this.client.isCms) {
                    try {
                        await draft.target.load(draft.target.maxVerbosity);
                    } catch (err) {
                        
                    }
                    draft.target.setDraft(draft);
                }
            } catch (err) {
                console.log('Failed to load draft');
            }
        }
    }
}
